import React, { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { useHistory } from '@fs/zion-router'
import SplashTransitionOverlay from '../components/SplashBase/SplashTransitionOverlay'

const SplashTransitionContext = createContext({
  startTransition: () => {},
  notifyReady: () => {},
  isTransitioning: false,
  isOverlayFlying: false,
})

export const useSplashTransition = () => useContext(SplashTransitionContext)

/**
 * Provider that manages splash page transitions at the app level.
 * This allows the transition animation to start BEFORE the route changes,
 * creating a seamless visual experience.
 */
export default function SplashTransitionProvider({ children }) {
  const history = useHistory()
  const transitionTokenRef = useRef(0)
  const activeTransitionIdRef = useRef(null)
  const pendingNavigationRef = useRef({ transitionId: null, url: null })

  const [state, setState] = useState({
    transitionData: null,
    isSplashMounted: false,
    isOverlayFlying: true, // true when overlay is in flight phase
  })

  // Cleanup any pending navigation when unmounting.
  useEffect(() => {
    return () => {
      pendingNavigationRef.current = { transitionId: null, url: null }
    }
  }, [])

  /**
   * Called by splash page when it's ready to be shown.
   */
  const notifySplashReady = useCallback(() => {
    setState((prev) => {
      // Ignore stale readiness signals if no transition is active.
      if (!prev.transitionData) return prev
      return {
        ...prev,
        isSplashMounted: true,
      }
    })
  }, [])

  /**
   * Called by overlay when the fly animation completes and it's ready to fade.
   * This signals that animations in the splash can begin.
   */
  const notifyOverlayFlyComplete = useCallback(
    (transitionId) => {
      // Ignore stale overlay callbacks from previous transitions.
      if (activeTransitionIdRef.current !== transitionId) return

      setState((prev) => {
        if (prev.transitionData?.transitionId !== transitionId) return prev
        return {
          ...prev,
          isOverlayFlying: false,
        }
      })

      const pending = pendingNavigationRef.current
      if (!pending?.url) return
      if (pending.transitionId !== transitionId) return

      // Navigate only once per transition.
      pendingNavigationRef.current = { transitionId: null, url: null }
      history.push(pending.url)
    },
    [history]
  )

  /**
   * Start a splash transition animation, then navigate after a brief delay.
   * @param {string} url - The destination URL
   * @param {Object} cardRect - Bounding rect of the clicked card {top, left, width, height}
   * @param {string} imageUrl - URL of the card's image
   * @param {string} colorTheme - Color theme for background
   * @param {number} targetIconWidth - Width of the target splash icon
   */
  const startTransition = useCallback(({ url, cardRect, imageUrl, colorTheme, targetIconWidth }) => {
    // Token guards against stale timeouts navigating after a newer transition.
    transitionTokenRef.current += 1
    const myToken = transitionTokenRef.current

    activeTransitionIdRef.current = myToken

    pendingNavigationRef.current = { transitionId: myToken, url }

    // Reset animation state and start new transition
    setState({
      transitionData: {
        transitionId: myToken,
        cardRect,
        imageUrl,
        colorTheme,
        targetIconWidth,
      },
      isSplashMounted: false,
      isOverlayFlying: true,
    })
  }, [])

  const handleTransitionComplete = useCallback(() => {
    activeTransitionIdRef.current = null
    pendingNavigationRef.current = { transitionId: null, url: null }
    setState({
      transitionData: null,
      isSplashMounted: false,
      isOverlayFlying: true,
    })
  }, [])

  // Check if a transition is active
  const isTransitioning = state.transitionData !== null

  const value = useMemo(
    () => ({
      startTransition,
      notifyReady: notifySplashReady,
      isTransitioning,
      isOverlayFlying: state.isOverlayFlying,
    }),
    [startTransition, notifySplashReady, isTransitioning, state.isOverlayFlying]
  )

  return (
    <SplashTransitionContext.Provider value={value}>
      {children}

      {/* Overlay renders at app level, persists across route changes */}
      {state.transitionData && (
        <SplashTransitionOverlay
          transitionId={state.transitionData.transitionId}
          cardRect={state.transitionData.cardRect}
          imageUrl={state.transitionData.imageUrl}
          backgroundColor={state.transitionData.colorTheme}
          onTransitionComplete={handleTransitionComplete}
          targetIconWidth={state.transitionData.targetIconWidth}
          onFlyComplete={notifyOverlayFlyComplete}
          isSplashMounted={state.isSplashMounted}
        />
      )}
    </SplashTransitionContext.Provider>
  )
}
